import { Lock } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CardMark } from "../../components/card/CardChrome";
import { HealthPassCardFront } from "../../components/card/HealthPassCardFront";
import { cardService, type CardDTO } from "../../services/api/cardService";
import { useI18nStore } from "../../stores/i18nStore";

/**
 * A card someone shared by link (/card/share/:token, made on the Shared links
 * page). It shows the card's front — who the holder is — and nothing of their
 * health record; the backend leaves the emergency summary out.
 *
 * The backend served these links from the start, but the app had no page at
 * that address: every shared link opened the login page.
 */

type State =
  | { kind: "loading" }
  | { kind: "card"; card: CardDTO }
  | { kind: "password"; wrong: boolean }
  | { kind: "gone" }
  | { kind: "missing" }
  | { kind: "error" };

function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

export const SharedCardPage: React.FC = () => {
  const { token = "" } = useParams<{ token: string }>();
  const { t } = useI18nStore();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [password, setPassword] = useState("");
  const [opening, setOpening] = useState(false);

  const open = useCallback(
    async (withPassword?: string) => {
      setOpening(true);
      try {
        const { cardData } = await cardService.accessShare(token, withPassword);
        setState({ kind: "card", card: cardData as CardDTO });
      } catch (error) {
        const status = statusOf(error);
        if (status === 401) {
          setState({ kind: "password", wrong: !!withPassword });
        } else if (status === 410) {
          setState({ kind: "gone" });
        } else if (status === 404) {
          setState({ kind: "missing" });
        } else {
          setState({ kind: "error" });
        }
      } finally {
        setOpening(false);
      }
    },
    [token],
  );

  useEffect(() => {
    void open();
  }, [open]);

  return (
    <div className="min-h-screen bg-ground px-4 pb-12 pt-8 text-ink">
      <div className="mx-auto max-w-[420px]">
        <div className="flex items-center gap-2 text-primary-600">
          <CardMark className="h-[22px] w-[22px]" />
          <span className="font-display text-base font-bold tracking-[0.04em] text-ink">
            HEALTH PASS
          </span>
        </div>
        <h1 className="mt-6 font-display text-[28px] font-extrabold leading-tight">
          {t.sharedCardTitle}
        </h1>

        {state.kind === "loading" && (
          <div className="mt-6 aspect-[85.6/54] w-full animate-pulse rounded-[18px] bg-primary-100" />
        )}

        {state.kind === "card" && (
          <>
            <p className="mt-2 text-[15px] text-slate-600">
              {t.sharedCardLead}
            </p>
            <div className="mt-6 flex justify-center">
              <HealthPassCardFront
                cardData={state.card}
                qrData={null}
                qrPlaceholder={t.sharedCardNoCode}
              />
            </div>
          </>
        )}

        {state.kind === "password" && (
          <form
            className="mt-6 rounded-[22px] bg-card p-5"
            onSubmit={(e) => {
              e.preventDefault();
              if (password) void open(password);
            }}
          >
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Lock className="h-4 w-4" aria-hidden="true" />
              {t.sharedCardPasswordLead}
            </div>
            <label className="mt-4 block text-[13px] font-semibold">
              {t.sharedCardPassword}
              <input
                type="password"
                autoComplete="off"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={state.wrong}
                className="mt-1.5 block min-h-[48px] w-full rounded-[14px] border border-input bg-card px-3.5 text-[15px] focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-600/20"
              />
            </label>
            {state.wrong && (
              <p role="alert" className="mt-2 text-sm text-travel-700">
                {t.sharedCardWrongPassword}
              </p>
            )}
            <button
              type="submit"
              disabled={!password || opening}
              className="mt-4 min-h-[52px] w-full rounded-2xl bg-ink text-[15px] font-bold text-white disabled:opacity-50"
            >
              {t.sharedCardOpen}
            </button>
          </form>
        )}

        {(state.kind === "gone" ||
          state.kind === "missing" ||
          state.kind === "error") && (
          <p
            role="alert"
            className="mt-6 rounded-[22px] bg-card p-5 text-[15px] text-slate-600"
          >
            {state.kind === "gone"
              ? t.sharedCardGone
              : state.kind === "missing"
                ? t.sharedCardMissing
                : t.sharedCardError}
          </p>
        )}
      </div>
    </div>
  );
};

export default SharedCardPage;
